import { JobSortableAttribute, ProjectsListParams } from '@contracts'
import {
  FilterComponentData,
  FilterReducerAction,
  FilterReducerState,
  IdValuePair,
  SelectFilterSelectOptionPayload,
  SelectFilterFreetextOptionPayload,
  InputFormat,
  SelectFilterComponentData,
  SetSortByPayload,
  ToggleFreetextFilterPayload,
  ToggleSelectFilterPayload,
  SelectFilterOptionPayload,
  EmptyDataMessage,
} from './GenericFiltersReducer.d'
import { updateSortByQueryParams } from 'utils'
import {
  extractPillComponentData,
  getDeepCopiedData,
  getDoesFilterObjectExist,
  getInitialOrderedFilters,
  removeAllFiltersFunction,
  replaceFreetextFilterFunction,
  selectFilterFreetextOptionReducerFunction,
  selectFilterSelectOptionReducerFunction,
} from './utils'
import { FilterId } from 'utils/constants'

export enum FilterActionTypes {
  INITIALISE_FILTER_OPTIONS = 'INITIALISE_FILTER_OPTIONS',
  SELECT_FILTER_SELECT_OPTION = 'SELECT_FILTER_SELECT_OPTION',
  SELECT_FILTER_FREETEXT_OPTION = 'SELECT_FILTER_FREETEXT_OPTION',
  ADD_SELECT_FILTER = 'ADD_SELECT_FILTER',
  ADD_FREETEXT_FILTER = 'ADD_FREETEXT_FILTER',
  REMOVE_SELECT_FILTER = 'REMOVE_SELECT_FILTER',
  REMOVE_FREETEXT_FILTER = 'REMOVE_FREETEXT_FILTER',
  REPLACE_SELECT_FILTER = 'REPLACE_FILTER',
  REPLACE_FREETEXT_FILTER = 'REPLACE_FREETEXT_FILTER',
  REMOVE_ALL_FILTERS = 'REMOVE_ALL_FILTERS',
  SET_SORT_BY = 'SET_SORT_BY',
  SET_EMPTY_DATA_MESSAGE = 'SET_EMPTY_DATA_MESSAGE',
  SET_IS_PRESELECT_COMPLETE = 'SET_IS_PRESELECT_COMPLETE',
  SET_ARE_DEPENDANT_FIELDS_READY = 'SET_ARE_DEPENDANT_FIELDS_READY',
}

export const getFilterReducerEmptyState = (
  reducerId: FilterId,
  filterObjects: FilterComponentData[],
  emptyDataMessage: string = '',
  isPreselectComplete: boolean = false,
  areDependantFieldsReady: boolean = false,
): FilterReducerState => {
  const index: Record<string, FilterComponentData> = {}
  filterObjects.forEach(filterObject => (index[filterObject.id] = filterObject))
  const emptyDataMessageObject: EmptyDataMessage = emptyDataMessage
    ? { shouldDisplay: true, message: emptyDataMessage }
    : { shouldDisplay: false, message: null }

  const initialState = {
    reducerId,
    orderedFilters: getInitialOrderedFilters(filterObjects),
    urlQueryParams: new URLSearchParams(),
    index,
    emptyDataMessage: emptyDataMessageObject,
    isPreselectComplete,
    areDependantFieldsReady,
  }

  return initialState
}

export const initialiseFilterOptions = (
  id: string,
  payload: Partial<Omit<SelectFilterComponentData, 'id'>>,
): FilterReducerAction => ({
  type: FilterActionTypes.INITIALISE_FILTER_OPTIONS,
  payload: { id, filterObject: payload },
})

export const selectFilterOption = (type: InputFormat, payload: SelectFilterOptionPayload): FilterReducerAction => {
  switch (type) {
    case 'text': {
      return {
        type: FilterActionTypes.SELECT_FILTER_FREETEXT_OPTION,
        payload,
      } as FilterReducerAction
    }
    case 'select': {
      return {
        type: FilterActionTypes.SELECT_FILTER_SELECT_OPTION,
        payload,
      } as FilterReducerAction
    }
  }
}

export const selectFilterSelectOption = (payload: SelectFilterSelectOptionPayload): FilterReducerAction => ({
  type: FilterActionTypes.SELECT_FILTER_SELECT_OPTION,
  payload,
})

export const selectFilterFreetextOption = (payload: SelectFilterFreetextOptionPayload): FilterReducerAction => ({
  type: FilterActionTypes.SELECT_FILTER_FREETEXT_OPTION,
  payload,
})

export const addSelectFilter = (payload: ToggleSelectFilterPayload): FilterReducerAction => ({
  type: FilterActionTypes.ADD_SELECT_FILTER,
  payload,
})

export const addFreetextFilter = (payload: ToggleFreetextFilterPayload): FilterReducerAction => ({
  type: FilterActionTypes.ADD_FREETEXT_FILTER,
  payload,
})

export const removeSelectFilter = (payload: ToggleSelectFilterPayload): FilterReducerAction => ({
  type: FilterActionTypes.REMOVE_SELECT_FILTER,
  payload,
})

export const removeFreetextFilter = (payload: ToggleFreetextFilterPayload): FilterReducerAction => ({
  type: FilterActionTypes.REMOVE_FREETEXT_FILTER,
  payload,
})

export const removeAllFilters = (payload: string = ''): FilterReducerAction => ({
  type: FilterActionTypes.REMOVE_ALL_FILTERS,
  payload,
})

export const replaceSelectFilter = (payload: ToggleSelectFilterPayload): FilterReducerAction => ({
  type: FilterActionTypes.REPLACE_SELECT_FILTER,
  payload,
})

export const replaceFreetextFilter = (payload: ToggleFreetextFilterPayload): FilterReducerAction => ({
  type: FilterActionTypes.REPLACE_FREETEXT_FILTER,
  payload,
})

export const setSortBy = (
  payload: SetSortByPayload<JobSortableAttribute | NonNullable<ProjectsListParams['sort_by']>> | null,
): FilterReducerAction => ({
  type: FilterActionTypes.SET_SORT_BY,
  payload,
})

export const setEmptyDataMessage = (payload: Partial<EmptyDataMessage>): FilterReducerAction => ({
  type: FilterActionTypes.SET_EMPTY_DATA_MESSAGE,
  payload,
})

export const setIsPreselectComplete = (payload: boolean): FilterReducerAction => ({
  type: FilterActionTypes.SET_IS_PRESELECT_COMPLETE,
  payload,
})

export const setAreDependantFieldsReady = (payload: boolean): FilterReducerAction => ({
  type: FilterActionTypes.SET_ARE_DEPENDANT_FIELDS_READY,
  payload,
})

const setUrlSearchParamsFromSelectObject = (url: URLSearchParams, id: string, filters: IdValuePair[]) => {
  url.set(id, filters.map(({ id }) => id).join(','))
}

export function filterReducer(state: FilterReducerState, action: FilterReducerAction) {
  const { type, payload } = action

  switch (type) {
    case FilterActionTypes.INITIALISE_FILTER_OPTIONS: {
      const { id, filterObject } = payload
      const { newIndex: index, newUrl } = getDeepCopiedData(state)
      const typesafeIndex = index[id] as SelectFilterComponentData

      const newFilterObject = { ...typesafeIndex, ...filterObject } as SelectFilterComponentData

      if (newFilterObject.defaultValue) {
        if (!newFilterObject.options.find(({ id }) => id === newFilterObject.defaultValue?.id)) {
          newFilterObject.options.unshift(newFilterObject.defaultValue!)
        }
      }
      newFilterObject.activeFilters = newFilterObject.activeFilters || []
      index[id] = newFilterObject

      if (newFilterObject.activeFilters.length)
        setUrlSearchParamsFromSelectObject(newUrl, id, newFilterObject.activeFilters)

      return {
        ...state,
        index: index,
        urlQueryParams: newUrl,
      }
    }

    // used to programmatically select a select filter option (e.g. when pre-selecting filters from the preserved filters)
    case FilterActionTypes.SELECT_FILTER_SELECT_OPTION: {
      return selectFilterSelectOptionReducerFunction(state, payload)
    }

    // used to programmatically select a freetext filter option (e.g. when pre-selecting filters from the preserved filters)
    case FilterActionTypes.SELECT_FILTER_FREETEXT_OPTION: {
      return selectFilterFreetextOptionReducerFunction(state, payload)
    }

    case FilterActionTypes.ADD_SELECT_FILTER: {
      const { filterObject, newFilterValue } = payload
      const { activeFilters, id } = filterObject
      const { newIndex, newUrl, newOrderedFilters: orderedFilters } = getDeepCopiedData(state)

      if (
        !getDoesFilterObjectExist(id, state.index) ||
        !!activeFilters.find(activeFilter => activeFilter.id === newFilterValue.id)
      )
        return state

      let newActiveFilters: IdValuePair[] = []
      let newOrderedFilters = orderedFilters

      if (newFilterValue.id === filterObject.defaultValue?.id) {
        newUrl.delete(id)
        newOrderedFilters = orderedFilters.filter(({ parentId }) => parentId !== id)
      } else {
        newActiveFilters = [...activeFilters, newFilterValue]
        newOrderedFilters.push(extractPillComponentData(filterObject, newFilterValue))
        setUrlSearchParamsFromSelectObject(newUrl, id, newActiveFilters)
      }

      newIndex[id] = { ...filterObject, activeFilters: newActiveFilters }

      return {
        ...state,
        index: newIndex,
        urlQueryParams: newUrl,
        orderedFilters: newOrderedFilters,
      }
    }

    case FilterActionTypes.ADD_FREETEXT_FILTER: {
      const { filterObject, newFilterValue } = payload
      const { activeFilters, id } = filterObject
      const formattedNewFilterValue = newFilterValue.trim()

      const { newIndex, newUrl, newOrderedFilters } = getDeepCopiedData(state)

      if (!getDoesFilterObjectExist(id, state.index) || activeFilters.includes(formattedNewFilterValue)) return state

      const newActiveFilters = [...activeFilters, formattedNewFilterValue]
      newOrderedFilters.push(extractPillComponentData(filterObject, formattedNewFilterValue))
      newUrl.set(id, newActiveFilters.join(','))

      newIndex[id] = { ...filterObject, activeFilters: newActiveFilters }

      return {
        ...state,
        index: newIndex,
        urlQueryParams: newUrl,
        orderedFilters: newOrderedFilters,
      }
    }

    case FilterActionTypes.REMOVE_SELECT_FILTER: {
      const { filterObject, newFilterValue } = payload
      const { activeFilters, id } = filterObject
      const { newIndex, newUrl, newOrderedFilters } = getDeepCopiedData(state)

      if (
        !getDoesFilterObjectExist(id, state.index) ||
        !activeFilters.find(activeFilter => activeFilter.id === newFilterValue.id)
      )
        return state

      const newActiveFilters = activeFilters.filter(activeFilter => activeFilter.id !== newFilterValue.id)
      const pillData = extractPillComponentData(filterObject, newFilterValue)
      const returnOrderedFilters = newOrderedFilters.filter(
        pillFilter =>
          !(pillData.id === pillFilter.parentId && (pillFilter.value as IdValuePair).id === newFilterValue.id),
      )

      setUrlSearchParamsFromSelectObject(newUrl, id, newActiveFilters)
      if (!newActiveFilters.length) newUrl.delete(id)

      newIndex[id] = {
        // FIXME: can I just simploify this to filterObject?
        ...(newIndex[id] as SelectFilterComponentData),
        activeFilters: newActiveFilters,
      }

      return {
        ...state,
        index: newIndex,
        urlQueryParams: newUrl,
        orderedFilters: returnOrderedFilters,
      }
    }

    case FilterActionTypes.REMOVE_FREETEXT_FILTER: {
      const { filterObject, newFilterValue } = payload
      const { activeFilters, id } = filterObject
      const { newIndex, newUrl, newOrderedFilters } = getDeepCopiedData(state)
      if (!getDoesFilterObjectExist(id, state.index) || !activeFilters.includes(newFilterValue)) return state

      const newActiveFilters = activeFilters.filter(filterValue => filterValue !== newFilterValue)
      const pillData = extractPillComponentData(filterObject, newFilterValue)
      const returnOrderedFilters = newOrderedFilters.filter(
        pillFilter => !(pillData.id === pillFilter.parentId && (pillFilter.value as string) === newFilterValue),
      )
      newUrl.set(id, newActiveFilters.join(','))

      if (!newActiveFilters.length) newUrl.delete(id)

      newIndex[id] = { ...filterObject, activeFilters: newActiveFilters }

      return {
        ...state,
        index: newIndex,
        urlQueryParams: newUrl,
        orderedFilters: returnOrderedFilters,
      }
    }
    case FilterActionTypes.REPLACE_SELECT_FILTER: {
      const { filterObject, newFilterValue } = payload
      const { id } = filterObject
      const { newIndex, newUrl } = getDeepCopiedData(state)
      if (!getDoesFilterObjectExist(id, state.index)) return state

      const newActiveFilters = [newFilterValue]

      setUrlSearchParamsFromSelectObject(newUrl, id, newActiveFilters)
      newIndex[id] = { ...filterObject, activeFilters: newActiveFilters }

      return {
        ...state,
        index: newIndex,
        urlQueryParams: newUrl,
      }
    }
    case FilterActionTypes.REPLACE_FREETEXT_FILTER: {
      return replaceFreetextFilterFunction(state, payload)
    }

    // Remove all all active filters, for a given filter select/freetext input element.
    case FilterActionTypes.REMOVE_ALL_FILTERS: {
      return removeAllFiltersFunction(state, payload)
    }

    case FilterActionTypes.SET_SORT_BY: {
      return {
        ...state,
        urlQueryParams: updateSortByQueryParams(new URLSearchParams(state.urlQueryParams), payload),
      }
    }

    case FilterActionTypes.SET_EMPTY_DATA_MESSAGE: {
      if (JSON.stringify(state.emptyDataMessage) === JSON.stringify(payload)) return state
      return {
        ...state,
        emptyDataMessage: { ...state.emptyDataMessage, ...payload },
      }
    }

    case FilterActionTypes.SET_IS_PRESELECT_COMPLETE: {
      return {
        ...state,
        isPreselectComplete: payload,
      }
    }
    case FilterActionTypes.SET_ARE_DEPENDANT_FIELDS_READY: {
      return {
        ...state,
        areDependantFieldsReady: payload,
      }
    }

    default:
      return state
  }
}
